import { CSSProperties } from 'react';
import styled from 'styled-components';
import type { RepresentationData } from '@ardoq/data-model';
import { IconName } from '@ardoq/icons';
import { s4, s8 } from '@ardoq/design-tokens';
import { ComponentMatrix2HeaderCellModel } from '../types';
import { ICON_WIDTH } from '../consts';
import { ComponentHeaderCardContent } from './componentHeaderCardContent';
import { getVisibleChildCount } from '../../util';

// `height: 100%` so the card content actually fills the (often tall)
// row track — without it the inner flex row sits at content height
// (e.g. 20px) at the top of the card regardless of `align-items`.
// `align-items` then switches between `center` (row at its minimum height —
// label centered in the band) and `flex-start` (row taller than its minimum —
// label pinned to top so it stays visible), per ARD-32762.
const CardContent = styled.div<{ $isMinHeight?: boolean }>`
  display: flex;
  align-items: ${({ $isMinHeight }) =>
    $isMinHeight === false ? 'flex-start' : 'center'};
  gap: ${s4};
  padding: ${s8};
  height: 100%;
  font-size: 14px;
  line-height: 20px;
`;

type RowHeaderComponentArgs = {
  representationData: RepresentationData;
  showExpander: boolean;
  isExpanded: boolean;
  headerCell: ComponentMatrix2HeaderCellModel;
  headerStyle: CSSProperties;
  isDark: boolean;
  isHighlighted: boolean;
  isContext: boolean;
  expanderClicked: VoidFunction;
  labelDoubleClicked: VoidFunction;
  wrapLabel?: boolean;
  isMinHeight?: boolean;
};

const RowHeaderComponent = ({
  representationData,
  showExpander,
  expanderClicked,
  labelDoubleClicked,
  isExpanded,
  headerCell,
  headerStyle,
  isDark,
  isHighlighted,
  isContext,
  wrapLabel = true,
  isMinHeight,
}: RowHeaderComponentArgs) => (
  <div style={{ ...headerStyle, pointerEvents: 'auto' }}>
    <CardContent $isMinHeight={isMinHeight}>
      <ComponentHeaderCardContent
        componentLabel={headerCell.group.label}
        representationData={representationData}
        otherLabels={headerCell.group.otherLabels}
        otherLabelWrappedLines={headerCell.otherLabelWrappedLines}
        childCount={
          showExpander && !isExpanded
            ? getVisibleChildCount(headerCell.group)
            : 0
        }
        showExpander={showExpander}
        isHighlighted={isHighlighted}
        isContext={isContext}
        isDark={isDark}
        iconSize={ICON_WIDTH}
        expanderIconName={
          isExpanded ? IconName.UNFOLD_LESS : IconName.UNFOLD_MORE
        }
        wrapLabel={wrapLabel}
        isMinHeight={isMinHeight}
        expanderClicked={expanderClicked}
        labelDoubleClicked={labelDoubleClicked}
      />
    </CardContent>
  </div>
);

export { RowHeaderComponent };
